import {
  type InsightsGenuiBento,
  type InsightsGenuiBentoCell,
  type GenuiWidgetKind,
  type GenuiColSpan,
} from "./genui-bento-types";
import { parseBentoSpec, parseBentoSpecStreaming } from "./parse-genui-bento";

export type InsightsGenuiTable = {
  type: "table";
  title?: string;
  columns: string[];
  rows: Array<Array<string | number | null | undefined>>;
};

export type InsightsGenuiBarPoint = {
  label: string;
  value: number;
};

export type InsightsGenuiBar = {
  type: "bar";
  title?: string;
  x: string;
  y: string;
  data: InsightsGenuiBarPoint[];
};

export type InsightsGenuiAbstain = {
  type: "abstain";
  summary: string;
  reasons?: string[];
  nextSteps?: string[];
};

export type InsightsGenuiSpec =
  InsightsGenuiTable | InsightsGenuiBar | InsightsGenuiBento | InsightsGenuiAbstain;

export type { InsightsGenuiBento, InsightsGenuiBentoCell, GenuiWidgetKind, GenuiColSpan };
export {
  GENUI_WIDGET_KINDS,
  GENUI_COL_SPANS,
  genuiColSpanClass,
  isGenuiWidgetKind,
  isGenuiColSpan,
} from "./genui-bento-types";
export { bentoHasDataWidget } from "./parse-genui-bento";

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function parseBarSpec(parsed: Record<string, unknown>): InsightsGenuiBar | null {
  const x = typeof parsed.x === "string" ? parsed.x.trim() : "";
  const y = typeof parsed.y === "string" ? parsed.y.trim() : "";
  if (!x || !y || !Array.isArray(parsed.data)) return null;

  const data: InsightsGenuiBarPoint[] = [];
  for (const row of parsed.data) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const value = asNumber(record[y]);
    if (value == null) continue;
    const rawLabel = record[x];
    const label = rawLabel == null || rawLabel === "" ? "—" : String(rawLabel);
    data.push({ label, value });
  }
  if (!data.length) return null;

  return {
    type: "bar",
    title: typeof parsed.title === "string" ? parsed.title : undefined,
    x,
    y,
    data,
  };
}

function parseTableSpec(parsed: Record<string, unknown>): InsightsGenuiTable | null {
  if (!Array.isArray(parsed.columns) || !Array.isArray(parsed.rows)) return null;
  return {
    type: "table",
    title: typeof parsed.title === "string" ? parsed.title : undefined,
    columns: parsed.columns.map((c) => String(c)),
    rows: parsed.rows.map((row) =>
      Array.isArray(row) ? row.map((cell) => (cell == null ? "" : cell)) : [],
    ),
  };
}

/** Parse a complete <render> body that is GenUI JSON (not HTML). */
export function tryParseGenuiSpec(raw: string): InsightsGenuiSpec | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (parsed.type === "table") return parseTableSpec(parsed);
    if (parsed.type === "bar") return parseBarSpec(parsed);
    if (parsed.type === "bento") return parseBentoSpec(parsed);
    if (parsed.type === "abstain") return parseAbstainSpec(parsed);
  } catch {
    return null;
  }
  return null;
}

function parseAbstainSpec(parsed: Record<string, unknown>): InsightsGenuiAbstain | null {
  const summary = typeof parsed.summary === "string" ? parsed.summary.trim() : "";
  if (!summary) return null;
  const reasons = Array.isArray(parsed.reasons)
    ? parsed.reasons
        .filter((r): r is string => typeof r === "string" && r.trim().length > 0)
        .map((r) => r.trim())
    : undefined;
  const nextSteps = Array.isArray(parsed.nextSteps)
    ? parsed.nextSteps
        .filter((r): r is string => typeof r === "string" && r.trim().length > 0)
        .map((r) => r.trim())
        .slice(0, 3)
    : undefined;
  return {
    type: "abstain",
    summary,
    ...(reasons?.length ? { reasons } : {}),
    ...(nextSteps?.length ? { nextSteps } : {}),
  };
}

/** Skip whitespace in a JSON fragment. */
function skipWs(s: string, i: number): number {
  while (i < s.length && /\s/.test(s[i]!)) i += 1;
  return i;
}

/**
 * Find `"key"` then `:` and return the index of the value start,
 * or -1 if the key assignment is not present yet.
 */
function findKeyValueStart(raw: string, key: string): number {
  const re = new RegExp(`"${key}"\\s*:`, "g");
  const m = re.exec(raw);
  if (!m || m.index === undefined) return -1;
  return skipWs(raw, m.index + m[0].length);
}

/** Parse a JSON string starting at `i` (must point at `"`). Returns end index exclusive. */
function readJsonString(raw: string, i: number): { value: string; end: number } | null {
  if (raw[i] !== '"') return null;
  let out = "";
  let j = i + 1;
  while (j < raw.length) {
    const ch = raw[j]!;
    if (ch === "\\") {
      if (j + 1 >= raw.length) return null; // incomplete escape
      const next = raw[j + 1]!;
      if (next === "u") {
        if (j + 5 >= raw.length) return null;
        out += String.fromCharCode(parseInt(raw.slice(j + 2, j + 6), 16));
        j += 6;
        continue;
      }
      const map: Record<string, string> = {
        '"': '"',
        "\\": "\\",
        "/": "/",
        b: "\b",
        f: "\f",
        n: "\n",
        r: "\r",
        t: "\t",
      };
      out += map[next] ?? next;
      j += 2;
      continue;
    }
    if (ch === '"') return { value: out, end: j + 1 };
    out += ch;
    j += 1;
  }
  return null; // unclosed string
}

/**
 * Extract a balanced JSON value starting at `i`.
 * Returns null if the value is incomplete (unbalanced / truncated).
 */
function readJsonValue(raw: string, i: number): { value: unknown; end: number } | null {
  i = skipWs(raw, i);
  if (i >= raw.length) return null;
  const ch = raw[i]!;

  if (ch === '"') {
    const s = readJsonString(raw, i);
    if (!s) return null;
    return { value: s.value, end: s.end };
  }

  if (ch === "{") {
    const obj: Record<string, unknown> = {};
    let j = i + 1;
    j = skipWs(raw, j);
    if (j < raw.length && raw[j] === "}") return { value: obj, end: j + 1 };
    while (j < raw.length) {
      j = skipWs(raw, j);
      const key = readJsonString(raw, j);
      if (!key) return null;
      j = skipWs(raw, key.end);
      if (raw[j] !== ":") return null;
      j = skipWs(raw, j + 1);
      const val = readJsonValue(raw, j);
      if (!val) return null;
      obj[key.value] = val.value;
      j = skipWs(raw, val.end);
      if (raw[j] === ",") {
        j += 1;
        continue;
      }
      if (raw[j] === "}") return { value: obj, end: j + 1 };
      return null;
    }
    return null;
  }

  if (ch === "[") {
    const arr: unknown[] = [];
    let j = i + 1;
    j = skipWs(raw, j);
    if (j < raw.length && raw[j] === "]") return { value: arr, end: j + 1 };
    while (j < raw.length) {
      const val = readJsonValue(raw, j);
      if (!val) return null;
      arr.push(val.value);
      j = skipWs(raw, val.end);
      if (raw[j] === ",") {
        j += 1;
        continue;
      }
      if (raw[j] === "]") return { value: arr, end: j + 1 };
      return null;
    }
    return null;
  }

  // number / true / false / null
  const lit = /^(?:-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null)/.exec(raw.slice(i));
  if (!lit) return null;
  const rawLit = lit[0];
  let value: unknown;
  if (rawLit === "true") value = true;
  else if (rawLit === "false") value = false;
  else if (rawLit === "null") value = null;
  else value = Number(rawLit);
  return { value, end: i + rawLit.length };
}

/**
 * Read complete elements from a JSON array that may still be open
 * (streaming). Stops before the first incomplete element.
 */
function readPartialArrayElements(raw: string, arrayValueStart: number): unknown[] {
  if (raw[arrayValueStart] !== "[") return [];
  const out: unknown[] = [];
  let j = skipWs(raw, arrayValueStart + 1);
  if (j < raw.length && raw[j] === "]") return out;
  while (j < raw.length) {
    const val = readJsonValue(raw, j);
    if (!val) break;
    out.push(val.value);
    j = skipWs(raw, val.end);
    if (raw[j] === ",") {
      j += 1;
      j = skipWs(raw, j);
      continue;
    }
    break;
  }
  return out;
}

function detectGenuiType(raw: string): "table" | "bar" | "bento" | null {
  // Prefer bento when both appear (nested table/bar inside bento cells).
  if (/"type"\s*:\s*"bento"/.test(raw)) return "bento";
  if (/"type"\s*:\s*"table"/.test(raw)) return "table";
  if (/"type"\s*:\s*"bar"/.test(raw)) return "bar";
  return null;
}

function streamingBento(raw: string): InsightsGenuiBento | null {
  const cellsStart = findKeyValueStart(raw, "cells");
  if (cellsStart < 0) {
    return { type: "bento", cols: 12, cells: [] };
  }
  const complete = readJsonValue(raw, cellsStart);
  if (complete && Array.isArray(complete.value)) {
    return parseBentoSpecStreaming({ type: "bento", cells: complete.value });
  }
  const partial = readPartialArrayElements(raw, cellsStart);
  return parseBentoSpecStreaming({ type: "bento", cells: partial });
}

function streamingTable(raw: string): InsightsGenuiTable | null {
  const columnsStart = findKeyValueStart(raw, "columns");
  if (columnsStart < 0) {
    const titleStart = findKeyValueStart(raw, "title");
    let title: string | undefined;
    if (titleStart >= 0) {
      const t = readJsonValue(raw, titleStart);
      if (t && typeof t.value === "string") title = t.value;
    }
    return { type: "table", title, columns: [], rows: [] };
  }

  const columnsVal = readJsonValue(raw, columnsStart);
  if (!columnsVal || !Array.isArray(columnsVal.value)) {
    const partial = readPartialArrayElements(raw, columnsStart);
    if (!partial.length) {
      return { type: "table", columns: [], rows: [] };
    }
    return {
      type: "table",
      columns: partial.map((c) => String(c)),
      rows: [],
    };
  }

  const columns = columnsVal.value.map((c) => String(c));
  let title: string | undefined;
  const titleStart = findKeyValueStart(raw, "title");
  if (titleStart >= 0) {
    const t = readJsonValue(raw, titleStart);
    if (t && typeof t.value === "string") title = t.value;
  }

  const rowsStart = findKeyValueStart(raw, "rows");
  if (rowsStart < 0) {
    return { type: "table", title, columns, rows: [] };
  }

  const completeRows = readJsonValue(raw, rowsStart);
  let rowsRaw: unknown[];
  if (completeRows && Array.isArray(completeRows.value)) {
    rowsRaw = completeRows.value;
  } else {
    rowsRaw = readPartialArrayElements(raw, rowsStart);
  }

  const rows = rowsRaw
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) => row.map((cell) => (cell == null ? "" : (cell as string | number))));

  return { type: "table", title, columns, rows };
}

function streamingBar(raw: string): InsightsGenuiBar | null {
  const xStart = findKeyValueStart(raw, "x");
  const yStart = findKeyValueStart(raw, "y");
  if (xStart < 0 || yStart < 0) {
    let title: string | undefined;
    const titleStart = findKeyValueStart(raw, "title");
    if (titleStart >= 0) {
      const t = readJsonValue(raw, titleStart);
      if (t && typeof t.value === "string") title = t.value;
    }
    return { type: "bar", title, x: "", y: "", data: [] };
  }

  const xVal = readJsonValue(raw, xStart);
  const yVal = readJsonValue(raw, yStart);
  if (!xVal || typeof xVal.value !== "string" || !yVal || typeof yVal.value !== "string") {
    return { type: "bar", x: "", y: "", data: [] };
  }
  const x = xVal.value.trim();
  const y = yVal.value.trim();

  let title: string | undefined;
  const titleStart = findKeyValueStart(raw, "title");
  if (titleStart >= 0) {
    const t = readJsonValue(raw, titleStart);
    if (t && typeof t.value === "string") title = t.value;
  }

  const dataStart = findKeyValueStart(raw, "data");
  if (dataStart < 0) {
    return { type: "bar", title, x, y, data: [] };
  }

  const completeData = readJsonValue(raw, dataStart);
  const elements =
    completeData && Array.isArray(completeData.value)
      ? completeData.value
      : readPartialArrayElements(raw, dataStart);

  const data: InsightsGenuiBarPoint[] = [];
  for (const row of elements) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const value = asNumber(record[y]);
    if (value == null) continue;
    const rawLabel = record[x];
    const label = rawLabel == null || rawLabel === "" ? "—" : String(rawLabel);
    data.push({ label, value });
  }

  return { type: "bar", title, x, y, data };
}

/**
 * Progressive GenUI parse for streaming <render> bodies.
 * Returns a partial table/bar shell as soon as `"type"` is known so the UI
 * can paint a widget and fill rows/bars as JSON arrives.
 */
export function tryParseGenuiSpecStreaming(raw: string): InsightsGenuiSpec | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;

  const complete = tryParseGenuiSpec(trimmed);
  if (complete) return complete;

  const kind = detectGenuiType(trimmed);
  if (kind === "bento") return streamingBento(trimmed);
  if (kind === "table") return streamingTable(trimmed);
  if (kind === "bar") return streamingBar(trimmed);
  return null;
}

/** True when the fragment looks like HTML (not GenUI JSON). */
export function looksLikeHtmlFragment(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed.startsWith("<");
}

/** Widget shape for incomplete <render> skeletons (HTML or GenUI before paint). */
export type InsightsRenderWidgetKind =
  "bento" | "abstain" | "table" | "bar" | "chart" | "cards" | "unknown";

/**
 * Best-effort kind from a streaming <render> body so InsightsRenderSkeleton can
 * mirror the eventual widget (bento grid, table rows, bars, Chart.js, KPI cards).
 * Caller: InsightsAnswer → InsightsRenderSkeleton while `seg.complete === false`.
 */
export function inferRenderWidgetKind(raw: string): InsightsRenderWidgetKind {
  const t = raw.trim();
  if (!t) return "unknown";

  if (/"type"\s*:\s*"bento"/.test(t)) return "bento";
  if (/"type"\s*:\s*"abstain"/.test(t)) return "abstain";
  if (/"type"\s*:\s*"table"/.test(t) || /<table\b/i.test(t)) return "table";
  if (/"type"\s*:\s*"bar"/.test(t)) return "bar";
  if (/\btype\s*:\s*['"]bar['"]/.test(t)) return "bar";
  if (
    /<canvas\b/i.test(t) ||
    /\bnew\s+Chart\b/.test(t) ||
    /\bChart\s*\(/.test(t) ||
    /\btype\s*:\s*['"](?:line|doughnut|pie|radar|polarArea)['"]/.test(t)
  ) {
    return "chart";
  }
  // Ranked CSS bars (design-system horizontal bars use width: N%).
  if (/width\s*:\s*\d+(?:\.\d+)?%/i.test(t) && /<(?:div|span)\b/i.test(t)) {
    return "bar";
  }
  const cardHits = t.match(/\bcard\b/gi)?.length ?? 0;
  if (cardHits >= 2) return "cards";
  if (/grid-template-columns/i.test(t) && (t.match(/<div\b/gi)?.length ?? 0) >= 4) {
    return "cards";
  }
  return "unknown";
}
